import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { UserProfile } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import { getAddressString } from 'features/Onboarding/common/getAddressString';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import CapitalDepositQuestionPage from '../../components/QuestionPage';
import sameAddressSchema, {
  SameAddressSchema,
} from '../../libs/sameAddressSchema';
import { CapitalDepositPage } from '../../types';
import locales from './locales';

type CapitalDepositSameAddressProps = {
  onSubmit: (values: SameAddressSchema) => void;
  formData: Partial<SameAddressSchema>;
  isSending: boolean;
  userProfile: Pick<
    UserProfile,
    'city' | 'country' | 'street' | 'streetAddition' | 'zip'
  >;
} & CapitalDepositPage;

const CapitalDepositSameAddress = asBentoPage<CapitalDepositSameAddressProps>(
  ({
    formData,
    isSending,
    Layout,
    onPrev,
    onSubmit,
    progress,
    userProfile,
  }) => {
    const {
      control,
      formState: { isValid },
      handleSubmit,
    } = useForm<SameAddressSchema>({
      defaultValues: formData,
      mode: 'onChange',
      resolver: zodResolver(sameAddressSchema),
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <CapitalDepositQuestionPage
          as="form"
          button={
            <Button
              icon="arrow-right"
              isDisabled={!isValid}
              isLoading={isSending}
              type="submit"
            >
              {locales.ctaSubmit}
            </Button>
          }
          onSubmit={handleSubmit(onSubmit)}
          progress={progress}
        >
          <Header title={locales.title} />

          <Controller
            control={control}
            name="hasSameAddress"
            render={({ field: { onChange, value } }) => (
              <Form.Radio.Group
                display="flex"
                flexDirection="column"
                onChange={(val) => onChange(val === 'true')}
                radioPosition="right"
                spacing="space-16"
                value={value === undefined ? undefined : String(value)}
                variant="outline"
              >
                <Form.Radio alignItems="start" padding="space-16" value="true">
                  <Form.Radio.Title>{locales.yes}</Form.Radio.Title>

                  <Form.Radio.Label>
                    {getAddressString(userProfile, true)}
                  </Form.Radio.Label>
                </Form.Radio>

                <Form.Radio padding="space-16" value="false">
                  <Form.Radio.Title>{locales.no}</Form.Radio.Title>
                </Form.Radio>
              </Form.Radio.Group>
            )}
          />
        </CapitalDepositQuestionPage>
      </Layout>
    );
  },
);

export default CapitalDepositSameAddress;
